import * as React from 'react';
import styled, { withTheme, keyframes } from 'styled-components';
import { rem } from 'polished';
import { __ } from 'react-i18n';
import { OutsideClick } from '../../../Defaults/OutsideClick';
import { Title } from '../../General/Title';
import { SvgIcon, IconType } from '../../General/SvgIcon';
import {
  FlexCol,
  FlexRowCenter,
  FlexColCenter,
} from '../../../Defaults/Flexbox/Flexbox';
import { Button } from '../../General/Button';
import { ModalItemsTable } from './ModalItemsTable';
import { MinAmountInfoText } from './MinAmountInfoText';
import {
  getZemplinProductDefaultInfo,
  getZemplinTrueCount,
  getElasticCategoryZemplinProductInfo,
} from '../../selectors';
import { ProductSearchItem, ZemplinLoaderWrapper } from '../../../..';
import { isSpojMatOrPreDrev } from '../../selectors';
import { WholePackagesOnly } from '../Items/WholePackageOnlyInfoText';
import { getSearchProductInfo } from '../../../../utilities/selectors';
import { getSymbolFromCurrency, prop } from '../../../../utilities';
import { StockInfo } from '../../Product/StockInfo';
import { SpecTable } from '../../ProductDetail/SpecTable';
import { DownloadsList } from '../../ProductDetail/DownloadsList';

export interface Props {
  handleClose: any;
  handleButtonClick: any;
  product: any;
  theme: any;
  isRequest: boolean;
  isLoggedIn: boolean;
  isFromCategory?: boolean;
  user?: any;
  isQuickBuy?: boolean;
  isInCart?: boolean;
  isOrdered?: boolean;
}

function AddToCartModal({
  handleClose,
  handleButtonClick,
  product,
  theme,
  isRequest,
  isLoggedIn,
  isFromCategory,
  user,
  isQuickBuy,
  isInCart,
  isOrdered,
}: Props) {
  const isB2B = user && user.b2b;
  const {
    productPackages,
    onStock,
    priceForUnpacking,
    isSpojMat,
    onStockExternal,
    defaultPackageSize,
  } = isFromCategory
    ? getElasticCategoryZemplinProductInfo(product, isLoggedIn)
    : getZemplinProductDefaultInfo(product);
  const { unit } = isFromCategory
    ? getElasticCategoryZemplinProductInfo(product, isLoggedIn)
    : getZemplinProductDefaultInfo(product);

  const initialCount = defaultPackageSize
    ? defaultPackageSize
    : productPackages
    ? productPackages
    : 1;

  const correctInitialCount =
    initialCount > onStock && onStock > 0 ? onStock : initialCount;

  React.useEffect(() => {
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = 'visible';
    };
  }, []);

  const minAmount = unit === 'Mks' ? 0.001 : 1;
  const [isFetching, setIsFetching] = React.useState(false);
  const [isMoreInfoOpen, setIsMoreInfoOpen] = React.useState(false);
  const [count, setCount] = React.useState(correctInitialCount);
  const trueCount = unit === 'Mks' ? +count * 1000 : count;
  const correctOnStock = onStock + onStockExternal;

  const handleAddToCart = (inputCount?: string) => {
    if ((inputCount && typeof inputCount === 'string') || count) {
      setIsFetching(true);
      handleButtonClick(
        getZemplinTrueCount(
          unit === 'Mks',
          parseFloat(
            inputCount && typeof inputCount === 'string' ? inputCount : count,
          ),
        ),
      );
    }
    // setIsFetching(false);
  };

  const handleCountChange = (
    newCount: string,
    shouldAddToCart: boolean = true,
  ) => {
    const withoutCommas = newCount.toString().replace(',', '.');
    const newCountNumber = parseFloat(withoutCommas);

    // const correctCount = isNaN(newCountNumber) ?  minAmount.toFixed(3): newCountNumber
    if (isNaN(newCountNumber)) {
      setCount(newCount);
      return;
    }
    if (!isRequest && newCountNumber > correctOnStock) {
      newCount = isB2B ? newCount : onStock;
    }

    if (shouldAddToCart) {
      if (parseFloat(newCount) < minAmount) {
        newCount = minAmount.toFixed(3);
      }
    }

    setCount(newCount);
  };

  const handleMoreClick = () => {
    setIsMoreInfoOpen(o => !o);
  };

  const handleEnterFunction = React.useCallback(
    event => {
      if (event.keyCode === 13) {
        handleAddToCart(count);
      }
    },
    [count],
  );

  const noHandlingFee: boolean = user
    ? user.no_handling_fee
      ? user.no_handling_fee === 0
        ? false
        : true
      : false
    : false;

  React.useEffect(() => {
    document.addEventListener('keydown', handleEnterFunction, false);

    return () => {
      document.removeEventListener('keydown', handleEnterFunction, false);
    };
  }, [count]);

  const wholePackagesOnly = product.product_detail
    ? product.product_detail.whole_packages_only === 1
      ? true
      : false
    : product.whole_packages_only === 1
    ? true
    : false;

  const lessThanMinimumPrice =
    priceForUnpacking * +trueCount < 10 &&
    count < productPackages &&
    productPackages >= 0.026 &&
    isSpojMat &&
    !(productPackages > onStock && parseFloat(count) === onStock)
      ? true
      : false;

  const {
    id,
    picture,
    url,
    name,
    currency,
    onStockTotal,
  } = getSearchProductInfo(product, 'product_detail.main_good');

  const attribs = prop(product, 'product_detail.attribs') || [];
  const downloads =
    prop(product, 'product_detail.publish.0.content.documents') || [];

  return (
    <AddAccountWrapper>
      <ModalStyle />
      <ModalHolder>
        <OutsideClickWrapper>
          <OutsideClick handleFunction={handleClose}>
            <Wrapper>
              <TitleWrapper>
                <Title marginTop={0} marginBottom={0}>
                  {__('Vyberte množstvo')}
                </Title>
                <SvgIcon
                  onClick={handleClose}
                  width={24}
                  height={24}
                  icon={IconType.close}
                  alt="Zavrieť okno"
                  title="Zavrieť okno"
                  fill={theme.colors.textPrimary}
                  cursor={'pointer'}
                />
              </TitleWrapper>
              {isQuickBuy && (
                <QuickBuyWrapper>
                  <ProductSearchItem
                    // tslint:disable-next-line:jsx-no-lambda
                    onClick={() => undefined}
                    searchTerm={''}
                    url={url}
                    name={name}
                    currency={getSymbolFromCurrency(currency)}
                    price={
                      onStockTotal > 0
                        ? prop(product, 'product_detail.main_good.priceinfo')
                          ? product.product_detail.main_good.priceinfo
                          : prop(product, 'main_good_price')
                        : null
                    }
                    key={id}
                    imgSrc={picture}
                    unit={prop(product, `helios_units`) || 'ks'}
                    isQuickBuy={isQuickBuy}
                    issale={
                      isB2B
                        ? prop(product, 'is_sale_b2b')
                        : prop(product, 'is_sale')
                    }
                    isInCart={isInCart}
                    isOrdered={isOrdered}
                  />
                  <StockInfo
                    // lessInfo={isMobile}
                    withoutExactStockCount={false}
                    justify={'flex-end'}
                    onStock={onStock}
                    onStockExternal={onStockExternal}
                    unit={unit}
                    bottomMargin={false}
                    storesInfo={prop(product, 'product_detail.stores_info')}
                    moveLeft={true}
                  />
                </QuickBuyWrapper>
              )}
              <ModalItemsTable
                isRequest={isRequest}
                count={count}
                handleCountChange={handleCountChange}
                products={[product]}
                isFetching={false}
                isLoggedIn={isLoggedIn}
                pca={true}
                isFromCategory={isFromCategory}
                noHandlingFee={noHandlingFee}
                isQuickBuy={isQuickBuy}
              />
              {wholePackagesOnly ? <WholePackagesOnly /> : ''}
              {lessThanMinimumPrice && !wholePackagesOnly && (
                <MinAmountInfoText
                  isWholePackagesWarningShown={wholePackagesOnly}
                />
              )}
              {isQuickBuy && (
                <MoreInfoButton onClick={handleMoreClick}>
                  {isMoreInfoOpen
                    ? __('Skryť dodatočné informácie')
                    : __('Zobraziť dodatočné informácie')}
                </MoreInfoButton>
              )}
              {isQuickBuy && isMoreInfoOpen && (
                <MoreInfoWrapper>
                  {downloads && downloads.length > 0 && (
                    <>
                      <h3>{__('Dokumenty na stiahnutie')}</h3>
                      <DownloadsList
                        downloads={downloads.map(d => ({
                          ...d,
                          document_path: `media/${prop(d, 'document_path')}`,
                        }))}
                      />
                      <br />
                    </>
                  )}
                  {attribs && attribs.length > 0 && (
                    <SpecTable attribs={attribs} isQuickBuy={true} />
                  )}
                </MoreInfoWrapper>
              )}

              {isFetching ? (
                <ZemplinLoaderWrapper height={120} />
              ) : (
                <AddButton
                  // tslint:disable-next-line:jsx-no-lambda
                  onClick={handleAddToCart}
                >
                  {isRequest ? __('Dopytovať') : __('Do košíka')}
                </AddButton>
              )}
            </Wrapper>
          </OutsideClick>
        </OutsideClickWrapper>
      </ModalHolder>
    </AddAccountWrapper>
  );
}

const OutsideClickWrapper = styled.div`
  width: 100%;
  max-width: ${rem(656)};
`;

const animateIn = keyframes`
   0% {
    opacity: 0;
    transform: scale(0.6) translateY(-8px);
  }
  
  100% {
    opacity: 1;
  }
`;

const Wrapper = styled(FlexCol)`
  padding: ${rem(32)};
  width: 100%;
  position: relative;
  opacity: 1;
  background: white;
  border-radius: ${rem(4)};
  margin: ${rem(16)} auto;
  box-shadow: ${({ theme }) => theme.boxShadow.default};
  display: block;
  animation: ${animateIn} 0.15s ease;
  overflow-y: auto;
  max-height: 95vh;

  ${({ theme }) => theme.mediab.m580`
      padding: ${rem(24)};
      margin: ${rem(16)};
      width: initial;
      height: 90vh;
  `};
`;

const AddAccountWrapper = styled.div``;

const TitleWrapper = styled(FlexRowCenter)`
  justify-content: space-between;
  margin-bottom: ${rem(32)};

  ${({ theme }) => theme.mediab.m580`
      margin-bottom: ${rem(8)};
  `};
`;

const QuickBuyWrapper = styled(FlexCol)`
  margin-bottom: ${rem(32)};
`;

const AddButton = styled(Button)`
  height: ${rem(48)};
  padding: 0 ${rem(16)};
  width: 100%;
  max-width: ${rem(160)};
  background-color: ${({ theme }) => theme.colors.secondary};
  color: ${({ theme }) => theme.colors.black};
  font-size: ${rem(14)};
  border-radius: ${rem(2)};
  margin: ${rem(32)} 0 0;

  &:disabled {
    background-color: #dcd6d6;
  }

  ${({ theme }) => theme.mediab.m580`
    max-width: initial;
    margin-top: ${rem(32)};
  `}
`;

const ModalHolder = styled(FlexColCenter)`
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  z-index: ${({ theme }) => theme.index.modal};
`;

const ModalStyle = styled.div`
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: #000000;
  opacity: 0.2;
  z-index: ${({ theme }) => theme.index.modal};
`;

const MoreInfoWrapper = styled(FlexCol)`
  margin-top: ${rem(16)};
  overflow-y: auto;
  height: ${rem(256)};
`;

const MoreInfoButton = styled.button`
  text-decoration: underline;
  background: transparent;
  border: 0;
  margin-top: ${rem(16)};
  color: #999999;
  font-size: ${rem(16)};
  cursor: pointer;
  padding: 0;

  &:hover {
    text-decoration: none;
  }
`;

const WithThemeComponent: any = withTheme(AddToCartModal);
export { WithThemeComponent as AddToCartModal };
